import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { Edit, Eye, Film, Image as ImageIcon, Plus, Search, Trash2, X } from "lucide-react";

import {
    createCapsuleRequest,
    deleteCapsuleRequest,
    getCapsulesRequest,
    getDomainesRequest,
    getThemesByDomaineRequest,
    resetCapsuleState,
    updateCapsuleRequest,
} from "../../../../redux/actions/capsuleActions";
import { API_BASE_URL } from "../../../../../api";
import CapsuleVideoModal from "./components/CapsuleVideoModal";

import "../RessourcesStyles/CapsuleManagement.css";

const emptyForm = {
    reference: "",
    nom: "",
    domaineId: "",
    themeId: "",
    description: "",
    file: null,
    video: null,
    picture: null,
    imageFile: null,
    ajouterAuMooc: false,
};

const getCapsuleImageUrl = (capsule) => {
    if (!capsule) return null;
    if (capsule.imageUrl) {
        return capsule.imageUrl.startsWith("http") ? capsule.imageUrl : `${API_BASE_URL}${capsule.imageUrl}`;
    }
    return capsule.picture ? `${API_BASE_URL}/api/formations/capsules/${capsule.id}/image` : null;
};

export default function CapsuleManagement() {
    const dispatch = useDispatch();
    const {
        capsules = [],
        domaines = [],
        themes = [],
        loading,
        success,
        lastAction,
    } = useSelector((state) => state.capsules || {});

    const [open, setOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [form, setForm] = useState(emptyForm);
    const [query, setQuery] = useState("");
    const [selectedCapsule, setSelectedCapsule] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    const capsulesList = useMemo(() => (Array.isArray(capsules) ? capsules : []), [capsules]);
    const domainesList = useMemo(() => (Array.isArray(domaines) ? domaines : []), [domaines]);
    const themesList = useMemo(() => (Array.isArray(themes) ? themes : []), [themes]);

    useEffect(() => {
        dispatch(getCapsulesRequest());
        dispatch(getDomainesRequest());
    }, [dispatch]);

    useEffect(() => {
        if (success && (lastAction === "create" || lastAction === "update")) {
            closeModal();
            dispatch(resetCapsuleState());
        }
    }, [success, lastAction, dispatch]);

    const filteredCapsules = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return capsulesList;

        return capsulesList.filter((capsule) =>
            `${capsule.nom || ""} ${capsule.reference || ""} ${capsule.domaineNom || ""} ${capsule.themeNom || ""}`
                .toLowerCase()
                .includes(q)
        );
    }, [capsulesList, query]);

    const getVideoUrl = (filename) => filename ? `${API_BASE_URL}/api/formations/capsules/videos/${filename}` : null;

    const openAdd = () => {
        dispatch(resetCapsuleState());
        setIsEdit(false);
        setCurrentId(null);
        setForm(emptyForm);
        setImagePreview(null);
        setOpen(true);
    };

    const openEdit = (capsule) => {
        dispatch(resetCapsuleState());
        setIsEdit(true);
        setCurrentId(capsule?.id ?? null);
        setForm({
            reference: capsule?.reference || "",
            nom: capsule?.nom || "",
            domaineId: capsule?.domaineId ? String(capsule.domaineId) : "",
            themeId: capsule?.themeId ? String(capsule.themeId) : "",
            description: capsule?.description || "",
            file: null,
            video: capsule?.video || null,
            picture: capsule?.picture || null,
            imageFile: null,
            ajouterAuMooc: Boolean(capsule?.ajouterAuMooc),
        });
        setImagePreview(getCapsuleImageUrl(capsule));

        if (capsule?.domaineId) {
            dispatch(getThemesByDomaineRequest(capsule.domaineId));
        }

        setOpen(true);
    };

    const closeModal = () => {
        setOpen(false);
        setIsEdit(false);
        setCurrentId(null);
        setForm(emptyForm);
        setImagePreview(null);
    };

    const handleDomaineChange = (value) => {
        setForm((prev) => ({ ...prev, domaineId: value, themeId: "" }));
        if (value) {
            dispatch(getThemesByDomaineRequest(value));
        }
    };

    const handleImageChange = (event) => {
        const file = event.target.files?.[0] || null;
        setForm((prev) => ({ ...prev, imageFile: file }));
        setImagePreview(file ? URL.createObjectURL(file) : null);
    };

    const validateForm = () => {
        if (!form.reference.trim()) return "Reference obligatoire";
        if (!form.nom.trim()) return "Nom obligatoire";
        if (!form.domaineId) return "Domaine obligatoire";
        if (!form.themeId) return "Theme obligatoire";
        if (!isEdit && !form.file) return "Le fichier video est obligatoire";
        return null;
    };

    const onSubmit = () => {
        const validationError = validateForm();
        if (validationError) {
            Swal.fire({ icon: "warning", text: validationError, timer: 1800, showConfirmButton: false });
            return;
        }

        const payload = {
            reference: form.reference,
            nom: form.nom,
            domaineId: form.domaineId,
            themeId: form.themeId,
            description: form.description,
            ajouterAuMooc: form.ajouterAuMooc,
            file: form.file,
            imageFile: form.imageFile,
        };

        if (isEdit && currentId !== null && currentId !== undefined) {
            dispatch(updateCapsuleRequest(currentId, payload));
        } else {
            dispatch(createCapsuleRequest(payload));
        }
    };

    const onDelete = (id) => {
        Swal.fire({
            title: "Supprimer ?",
            text: "Cette action est irreversible",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Oui",
            cancelButtonText: "Annuler",
            confirmButtonColor: "#ee645f",
            cancelButtonColor: "#204385",
        }).then((result) => {
            if (result.isConfirmed) dispatch(deleteCapsuleRequest(id));
        });
    };

    return (
        <div className="cat-page capsule-page">
            <div className="cat-header">
                <h2>Capsules</h2>
            </div>

            <div className="cat-card">
                <div className="cat-body">
                    <div className="cat-section-top">
                        <div className="cat-search-wrap">
                            <Search size={14} className="cat-search-icon" />
                            <input
                                type="text"
                                className="cat-search"
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                                placeholder="Rechercher par nom, reference, domaine ou theme..."
                            />
                        </div>

                        <button className="cat-btn cat-btn-primary" onClick={openAdd} type="button">
                            <Plus size={16} /> Ajouter
                        </button>
                    </div>

                    <div className="cat-table-wrap">
                        <table className="cat-table">
                            <thead>
                            <tr>
                                <th>Reference</th>
                                <th>Nom de capsule</th>
                                <th>Domaine</th>
                                <th>Theme</th>
                                <th>Ajouter au MOOC</th>
                                <th>Video</th>
                                <th className="is-right" style={{ width: 120 }}>Actions</th>
                            </tr>
                            </thead>
                            <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="cat-loading">Chargement...</td>
                                </tr>
                            ) : filteredCapsules.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="cat-empty-cell">Aucune capsule trouvee</td>
                                </tr>
                            ) : (
                                filteredCapsules.map((capsule) => (
                                    <tr key={capsule.id}>
                                        <td><span className="cat-badge cat-badge--blue">{capsule.reference}</span></td>
                                        <td>
                                            <div className="capsule-title">
                                                <Film size={14} />
                                                <span>{capsule.nom}</span>
                                            </div>
                                        </td>
                                        <td>{capsule.domaineNom || "-"}</td>
                                        <td>{capsule.themeNom || "-"}</td>
                                        <td>
                                            <span className={`cat-pill ${capsule.ajouterAuMooc ? "ok" : "off"}`}>
                                                {capsule.ajouterAuMooc ? "Oui" : "Non"}
                                            </span>
                                        </td>
                                        <td>
                                            <button
                                                type="button"
                                                className="capsule-video-link capsule-video-button"
                                                onClick={() => setSelectedCapsule(capsule)}
                                            >
                                                <Eye size={14} /> Voir
                                            </button>
                                        </td>
                                        <td className="is-right">
                                            <div className="cat-actions">
                                                <button className="cat-icon-btn" title="Modifier" onClick={() => openEdit(capsule)} type="button">
                                                    <Edit size={16} />
                                                </button>
                                                <button className="cat-icon-btn cat-icon-btn--danger" title="Supprimer" onClick={() => onDelete(capsule.id)} type="button">
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {open && (
                <div className="cat-modal-overlay" onClick={closeModal}>
                    <div className="cat-modal capsule-modal" onClick={(event) => event.stopPropagation()}>
                        <div className="cat-modal-header">
                            <h3>
                                <Film size={16} style={{ color: "#ee645f" }} />
                                {isEdit ? "Modifier une capsule" : "Ajouter une capsule"}
                            </h3>
                            <button className="cat-x" onClick={closeModal} type="button"><X size={18} /></button>
                        </div>

                        <div className="cat-modal-body">
                            <div className="cat-grid">
                                <div className="cat-field">
                                    <label>Reference / Code *</label>
                                    <input
                                        value={form.reference}
                                        onChange={(event) => setForm({ ...form, reference: event.target.value })}
                                        placeholder="CAP-2026"
                                    />
                                </div>

                                <div className="cat-field">
                                    <label>Nom de capsule *</label>
                                    <input
                                        value={form.nom}
                                        onChange={(event) => setForm({ ...form, nom: event.target.value })}
                                        placeholder="Nom de capsule"
                                    />
                                </div>

                                <div className="cat-field">
                                    <label>Domaine *</label>
                                    <select value={form.domaineId} onChange={(event) => handleDomaineChange(event.target.value)}>
                                        <option value="">Selectionner un domaine</option>
                                        {domainesList.map((domaine) => (
                                            <option key={domaine.id} value={domaine.id}>{domaine.nom}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="cat-field">
                                    <label>Theme *</label>
                                    <select
                                        value={form.themeId}
                                        disabled={!form.domaineId}
                                        onChange={(event) => setForm({ ...form, themeId: event.target.value })}
                                    >
                                        <option value="">Selectionner un theme</option>
                                        {themesList.map((theme) => (
                                            <option key={theme.id} value={theme.id}>{theme.nom}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="cat-field cat-span-2">
                                    <label>Description</label>
                                    <textarea
                                        rows={4}
                                        value={form.description}
                                        onChange={(event) => setForm({ ...form, description: event.target.value })}
                                        placeholder="Description..."
                                    />
                                </div>

                                <div className="cat-field">
                                    <label>Choisir un fichier video {isEdit ? "" : "*"}</label>
                                    <div className="capsule-upload">
                                        {form.video && !form.file && (
                                            <a href={getVideoUrl(form.video)} target="_blank" rel="noreferrer" className="capsule-current-file">
                                                Video actuelle
                                            </a>
                                        )}
                                        <input
                                            type="file"
                                            accept="video/mp4,video/quicktime,video/x-msvideo,video/webm,video/x-matroska,.mp4,.mov,.avi,.webm,.mkv"
                                            onChange={(event) => setForm({ ...form, file: event.target.files?.[0] || null })}
                                        />
                                        <span className="cat-file-pill">
                                            {form.file ? form.file.name : "Aucun fichier n'a ete selectionne"}
                                        </span>
                                    </div>
                                </div>

                                <div className="cat-field">
                                    <label>Image de couverture</label>
                                    <div className="capsule-upload">
                                        <input
                                            type="file"
                                            accept="image/png,image/jpeg,image/jpg,image/webp,.png,.jpg,.jpeg,.webp"
                                            onChange={handleImageChange}
                                        />
                                        <span className="cat-file-pill">
                                            {form.imageFile ? form.imageFile.name : (form.picture ? "Image actuelle conservee" : "Aucune image selectionnee")}
                                        </span>
                                        <div className="capsule-picture-preview">
                                            {imagePreview ? (
                                                <img src={imagePreview} alt="Apercu capsule" />
                                            ) : (
                                                <div className="capsule-picture-empty">
                                                    <ImageIcon size={18} />
                                                    <span>Aucune image</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="cat-field">
                                    <label>Ajouter au MOOC</label>
                                    <div className="capsule-radio-group">
                                        <label className="capsule-radio">
                                            <input
                                                type="radio"
                                                checked={form.ajouterAuMooc === true}
                                                onChange={() => setForm({ ...form, ajouterAuMooc: true })}
                                            />
                                            Oui
                                        </label>
                                        <label className="capsule-radio">
                                            <input
                                                type="radio"
                                                checked={form.ajouterAuMooc === false}
                                                onChange={() => setForm({ ...form, ajouterAuMooc: false })}
                                            />
                                            Non
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="cat-modal-footer">
                            <button className="cat-btn cat-btn-light" onClick={closeModal} disabled={loading} type="button">Annuler</button>
                            <button className="cat-btn cat-btn-blue" onClick={onSubmit} disabled={loading} type="button">
                                {loading ? "Enregistrement..." : "Enregistrer"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <CapsuleVideoModal
                isOpen={Boolean(selectedCapsule)}
                capsule={selectedCapsule}
                onClose={() => setSelectedCapsule(null)}
            />
        </div>
    );
}
